import React, { useState, useEffect, useRef } from "react";
import { Scale, Check, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { emitBodyweightUpdate, onBodyweightUpdate } from "./syncEvents";

const BodyweightBadge = () => {
  const [bodyweight, setBodyweight] = useState(null);
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const [unit, setUnit] = useState("lbs");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    fetchBodyweight();
    const unsub = onBodyweightUpdate((bw) => setBodyweight(bw));
    return unsub;
  }, []);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setEditing(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [editing]);

  const fetchBodyweight = async () => {
    try {
      const res = await fetch(`${API}/progression/bodyweight`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.bodyweight) setBodyweight(data.bodyweight);
      }
    } catch {}
  };

  const openEditor = () => {
    setInputVal(bodyweight ? bodyweight.toString() : "");
    setUnit("lbs");
    setEditing(true);
  };

  const handleSave = async () => {
    let bw = parseFloat(inputVal);
    if (!bw || bw <= 0) return toast.error("Enter a valid bodyweight");
    if (unit === "kg") bw = Math.round(bw * 2.20462 * 10) / 10;

    setSaving(true);
    try {
      const res = await fetch(`${API}/progression/bodyweight`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ bodyweight: bw }),
      });
      if (res.ok) {
        setBodyweight(bw);
        emitBodyweightUpdate(bw);
        setEditing(false);
        toast.success("Bodyweight updated");
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") setEditing(false);
  };

  return (
    <div className="relative inline-block" data-testid="bodyweight-badge-container">
      <button
        onClick={openEditor}
        className="flex items-center gap-1.5 bg-[#141B2D] hover:bg-[#1E293B] border border-[#334155] hover:border-primary/50 rounded-full px-3 py-1.5 transition-all group"
        data-testid="bodyweight-badge"
      >
        <Scale className="w-3.5 h-3.5 text-primary" />
        <span className="text-sm font-semibold text-white">
          {bodyweight ? `${bodyweight} lbs` : "Set Weight"}
        </span>
      </button>

      {editing && (
        <div
          ref={popoverRef}
          className="absolute top-full mt-2 right-0 z-50 bg-[#0F172A] border border-[#334155] rounded-xl shadow-2xl p-4 w-64 animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="bodyweight-popover"
        >
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Update Bodyweight</p>
          <div className="flex gap-2 mb-3">
            <input
              ref={inputRef}
              type="number"
              step="0.1"
              min="50"
              max="500"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={unit === "lbs" ? "185" : "84"}
              className="flex-1 bg-[#0B0F1A] border border-[#334155] rounded-lg px-3 py-2 text-white text-sm font-medium placeholder-gray-600 focus:border-primary focus:outline-none"
              data-testid="bodyweight-popover-input"
            />
            <div className="flex rounded-lg overflow-hidden border border-[#334155]">
              {["lbs", "kg"].map(u => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setUnit(u)}
                  className={`px-2.5 py-2 text-xs font-bold transition-colors ${unit === u ? "bg-primary text-white" : "bg-[#0B0F1A] text-gray-500 hover:text-white"}`}
                  data-testid={`bodyweight-popover-unit-${u}`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-1 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white py-2 rounded-lg text-xs font-bold transition-colors"
              data-testid="bodyweight-popover-save"
            >
              <Check className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-2 bg-[#1E293B] hover:bg-[#334155] text-gray-400 rounded-lg text-xs font-bold transition-colors"
              data-testid="bodyweight-popover-cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[10px] text-gray-600 mt-2">Syncs with nutrition & profile</p>
        </div>
      )}
    </div>
  );
};

export default BodyweightBadge;
